/**
 * PAYMENT DETAILS' WORDS AND RULES, SHARED (design 25, T22) - by the page (./PayoutMethodRoute.tsx),
 * its card at the top of Payments and at the end of Author profile (./PaymentDetailsSummaryCard.tsx),
 * and anything else that reminds an author (the Overview's row). One place, so the badge, the
 * missing items and the needed-by day read the same wherever they appear.
 *
 * NOTHING HERE READS A CLOCK (C11). "Today", where a sentence depends on it, is an instant the
 * server sent - the payments view's `computedAt` - and a day is compared as text, `YYYY-MM-DD`.
 *
 * NOTHING HERE WRITES A PAYOUT RULE IN. The minimum and the currency come from the payments view
 * (`threshold.thresholdMinor`, `currency`), as the Payments page's own rules do; without that read
 * the sentence names no figure.
 */
import { formatDay, formatMoney } from '../../features/cabinet/format';
import { AGREEMENT_DOCUMENT } from '../../features/cabinet/api/cabinet-api';
import { i18n } from '../../i18n';
import type {
  Agreement,
  CountryList,
  PaymentDetails,
  PaymentDetailsPart,
} from '../../features/cabinet/api/cabinet-api';

/** The cabinet's display language as a locale for `Intl`: its English is American English. */
function displayLocale(): string {
  return i18n.language === 'en' ? 'en-US' : i18n.language;
}

/** What the badge says. Colour is the second channel; these words are the first. */
export function completenessWords(complete: boolean): string {
  return complete ? i18n.t('payments:details.complete') : i18n.t('payments:details.notComplete');
}

/** Each missing part the "Missing: ..." line names in words (`details.part.<part>`). */
const MISSING_PARTS: ReadonlySet<string> = new Set<PaymentDetailsPart>([
  'legal_name',
  'country',
  'paypal_email',
  'details_true',
  'payout_terms',
  'payment_data_notice',
]);

function isMissingPart(part: string): part is PaymentDetailsPart {
  return MISSING_PARTS.has(part);
}

/** "Missing: legal name, country of residence" - or nothing when nothing is. */
export function missingLine(missing: readonly string[] | undefined): string | undefined {
  const named = (missing ?? []).map((part) =>
    isMissingPart(part) ? i18n.t(`payments:details.part.${part}`) : part.replaceAll('_', ' '),
  );
  return named.length === 0
    ? undefined
    : i18n.t('payments:details.missing', {
        parts: named.join(i18n.t('payments:details.listSeparator')),
      });
}

/** True when the record is complete, as the server worked it out. */
export function isComplete(method: PaymentDetails): boolean {
  return method.isSet && method.complete === true;
}

/**
 * The needed-by line (design 25, P6/P7), while the record is not complete and a day is set. Before
 * the day - or when the cabinet was not told what day it is - the payouts go on as usual; from it,
 * an incomplete record holds the payout back and the money rolls over.
 */
export function requiredFromLine(
  requiredFrom: string | null | undefined,
  today: string | null | undefined,
): string | undefined {
  if (requiredFrom === undefined || requiredFrom === null) {
    return undefined;
  }
  const day = formatDay(requiredFrom);
  if (today !== undefined && today !== null && today.slice(0, 10) >= requiredFrom) {
    return i18n.t('payments:details.since', { day });
  }
  return i18n.t('payments:details.neededBy', { day });
}

/** The figures the intro needs, as the payments view serves them. */
export interface PayoutRule {
  readonly currency: string;
  readonly thresholdMinor: number;
}

/** "US dollars", "euros": the currency's plural name in the cabinet's language. */
function currencyName(currency: string): string {
  try {
    const parts = new Intl.NumberFormat(displayLocale(), {
      style: 'currency',
      currency,
      currencyDisplay: 'name',
    }).formatToParts(2);
    return parts.find((part) => part.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
}

/** The page's intro, from the served rule; without it, the same rule with no figure. */
export function payoutIntro(rule: PayoutRule | undefined): string {
  if (rule === undefined) {
    return i18n.t('payments:details.intro');
  }
  return i18n.t('payments:details.introWithRule', {
    currency: currencyName(rule.currency),
    minimum: formatMoney(rule.thresholdMinor, rule.currency),
  });
}

/** One `Intl.DisplayNames` per display language, made the first time that language asks. */
const regionNamesByLocale = new Map<string, Intl.DisplayNames | null>();

function regionNamesIn(locale: string): Intl.DisplayNames | null {
  let names = regionNamesByLocale.get(locale);
  if (names === undefined) {
    try {
      names = new Intl.DisplayNames(locale, { type: 'region', fallback: 'none' });
    } catch {
      names = null;
    }
    regionNamesByLocale.set(locale, names);
  }
  return names;
}

/**
 * A country's name in the cabinet's language - American English for English (`Intl.DisplayNames`),
 * or the served name where the browser has none - and the code itself as the last resort.
 */
export function countryName(code: string, served?: string): string {
  let named: string | undefined;
  try {
    named = regionNamesIn(displayLocale())?.of(code);
  } catch {
    named = undefined;
  }
  return named ?? served ?? code;
}

/** The picker's options: every served country by its name in the cabinet's language, in that order. */
export function countryOptions(
  countries: CountryList | undefined,
  onFile: string | null | undefined,
): { value: string; label: string }[] {
  const items = countries?.items ?? [];
  const options = items.map((item) => ({
    value: item.code,
    label: countryName(item.code, item.label),
  }));
  // A country on file that the list does not carry (or a list that did not load) is still shown.
  if (
    onFile !== undefined &&
    onFile !== null &&
    !options.some((option) => option.value === onFile)
  ) {
    options.push({ value: onFile, label: countryName(onFile) });
  }
  const locale = displayLocale();
  return options.sort((a, b) => a.label.localeCompare(b.label, locale));
}

/** The day of an instant the server sent, in the author's own time zone: "Oct 11, 2026". */
export function dayOfInstant(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(displayLocale(), { dateStyle: 'medium' }).format(parsed);
}

/**
 * The payout documents, in the served order. One with no version in force (`live` null) is served
 * only when this author accepted a version of it, and is shown as accepted (P10).
 */
export function payoutDocuments(items: readonly Agreement[] | undefined): readonly Agreement[] {
  return (items ?? []).filter(
    (item) => item.blocks === 'payout' && (item.live !== null || item.accepted !== null),
  );
}

/**
 * True when there is nothing to tick: the version in force is the one accepted, or none is in force
 * and one was accepted before - a plain "accepted" row, with no link when nothing is in force.
 */
export function acceptedLive(document: Agreement): boolean {
  return (
    document.accepted !== null &&
    (document.live === null || document.accepted.versionLabel === document.live.versionLabel)
  );
}

/** Each payout document's checkbox and link, in the page's words; another document its title. */
export function documentWords(document: Agreement): {
  readonly agree: string;
  readonly read: string;
  readonly name: string;
} {
  const known =
    document.documentCode === AGREEMENT_DOCUMENT.payoutTerms
      ? 'payoutTerms'
      : document.documentCode === AGREEMENT_DOCUMENT.paymentDataNotice
        ? 'paymentDataNotice'
        : null;
  if (known !== null) {
    return {
      agree: i18n.t(`payments:details.documents.${known}.agree`),
      read: i18n.t(`payments:details.documents.${known}.read`),
      name: i18n.t(`payments:details.documents.${known}.name`),
    };
  }
  return {
    agree: i18n.t('payments:details.documents.other.agree', { title: document.title }),
    read: i18n.t('payments:details.documents.other.read', { title: document.title }),
    name: document.title,
  };
}

/**
 * The check made on save, in the page's words: each message's place in the catalogue, translated
 * where it is shown, so a message on screen follows the cabinet's language. Each says what to do.
 */
export const FORM_ERRORS = {
  legalName: 'details.errors.legalName',
  legalNameTooLong: 'details.errors.legalNameTooLong',
  country: 'details.errors.country',
  email: 'details.errors.email',
  confirm: 'details.errors.confirm',
} as const;

export type FormError = (typeof FORM_ERRORS)[keyof typeof FORM_ERRORS];
